import { useEffect, useRef, useState } from "react";
import { FileUp, Info, Loader2, Palette, Save, Sparkles, X } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useAiFeature } from "@/lib/ai";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/** The company's quote design (see the server's QuoteStyle) */
export interface QuoteStyle {
  font: "sans" | "serif" | "modern";
  colors: Record<"accent" | "title" | "text" | "muted" | "border" | "table_head_bg" | "table_head_text" | "zebra" | "total_bg" | "total_text", string>;
  sizes: { base: number; title: number };
  title_text: string;
  header: "company_left" | "title_left" | "centered";
  table: "grid" | "lines" | "zebra";
  page_numbers: "none" | "bottom_center" | "bottom_right";
  upper_labels: boolean;
}

const STANDARD: QuoteStyle = {
  font: "sans",
  colors: { accent: "#312e81", title: "#312e81", text: "#1e293b", muted: "#64748b", border: "#e2e8f0", table_head_bg: "#312e81", table_head_text: "#ffffff", zebra: "#f8fafc", total_bg: "#ffffff", total_text: "#0f172a" },
  sizes: { base: 9, title: 18 },
  title_text: "QUOTATION",
  header: "company_left",
  table: "zebra",
  page_numbers: "bottom_right",
  upper_labels: true,
};

const PALETTES: Array<{ name: string; accent: string; zebra: string }> = [
  { name: "Indigo", accent: "#312e81", zebra: "#f8fafc" },
  { name: "Teal", accent: "#0f766e", zebra: "#f0fdfa" },
  { name: "Maroon", accent: "#7f1d1d", zebra: "#fef2f2" },
  { name: "Forest", accent: "#166534", zebra: "#f0fdf4" },
  { name: "Ocean", accent: "#1e40af", zebra: "#eff6ff" },
  { name: "Charcoal", accent: "#27272a", zebra: "#f4f4f5" },
];

const COLOR_LABELS: Array<[keyof QuoteStyle["colors"], string]> = [
  ["accent", "Main colour"],
  ["title", "Title"],
  ["text", "Body text"],
  ["muted", "Labels"],
  ["table_head_bg", "Table header"],
  ["table_head_text", "Table header text"],
  ["zebra", "Shaded rows"],
  ["border", "Lines"],
  ["total_bg", "Total row"],
  ["total_text", "Total row text"],
];

function Choice<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: Array<[T, string]>; onChange: (v: T) => void }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium">{label}</div>
      <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={label}>
        {options.map(([v, text]) => (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={value === v}
            onClick={() => onChange(v)}
            className={cn("rounded-md border px-2.5 py-1 text-xs transition-colors", value === v ? "border-primary bg-primary/10 font-semibold text-primary" : "hover:bg-muted/60")}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Settings → Quote design: how printed quotes look (font, colours, title, table), edited by hand or made from a sample
 * quote. New quotes take it; quotes already made keep theirs. Pages are the letterhead when one is set up, else white.
 */
export function QuoteDesignPage() {
  const { success, error } = useToast();
  const fromSampleOn = useAiFeature("TEMPLATE_FROM_SAMPLE");
  const [loading, setLoading] = useState(true);
  const [style, setStyle] = useState<QuoteStyle | null>(null);
  const [terms, setTerms] = useState("");
  const [savedTerms, setSavedTerms] = useState("");
  const [hasLetterhead, setHasLetterhead] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [notes, setNotes] = useState<string[] | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    api
      .get("/api/QuoteDesign")
      .then((r) => {
        const d = unwrapObject<{ style: QuoteStyle | null; default_terms: string; has_letterhead: boolean }>(r);
        setStyle(d.style);
        setTerms(d.default_terms || "");
        setSavedTerms(d.default_terms || "");
        setHasLetterhead(d.has_letterhead);
      })
      .catch((e) => error("Couldn't load the quote design", e.message || "Please try again"))
      .finally(() => setLoading(false));
  }, []);

  // The sample quote as a PDF, redrawn a moment after the last change
  useEffect(() => {
    if (loading) return;
    let stale = false;
    const t = setTimeout(async () => {
      setPdfBusy(true);
      try {
        const res = await fetch("/api/QuoteDesign/PreviewPdf", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ style, terms }) });
        if (!res.ok) throw new Error();
        const url = URL.createObjectURL(await res.blob());
        if (stale) return URL.revokeObjectURL(url);
        setPdfUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return url;
        });
      } catch {
        /* the old preview stays */
      } finally {
        if (!stale) setPdfBusy(false);
      }
    }, 700);
    return () => {
      stale = true;
      clearTimeout(t);
    };
  }, [style, terms, loading]);

  const change = (next: QuoteStyle | null) => {
    setStyle(next);
    setDirty(true);
  };
  const set = (patch: Partial<QuoteStyle>) => style && change({ ...style, ...patch });

  const fromSample = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      error("The sample is too big", "Keep it under 15 MB (the first page is enough)");
      return;
    }
    setReading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const d = unwrapObject<{ style: QuoteStyle; terms: string; notes: string[] }>(await api.upload("/api/QuoteDesign/FromSample", fd));
      setStyle(d.style);
      if (d.terms) setTerms(d.terms);
      setNotes(d.notes || []);
      setDirty(true);
      success("Design ready to check", "Adjust it, then save");
    } catch (e: any) {
      error("Couldn't read the sample", e.message || "Please try another file");
    } finally {
      setReading(false);
      if (input.current) input.current.value = "";
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const d = unwrapObject<{ style: QuoteStyle | null; default_terms: string }>(await api.put("/api/QuoteDesign", { style, ...(terms !== savedTerms ? { default_terms: terms } : {}) }));
      setStyle(d.style);
      setSavedTerms(d.default_terms || "");
      setTerms(d.default_terms || "");
      setDirty(false);
      setNotes(null);
      success("Quote design saved", "New quotes use it; quotes already made keep theirs");
    } catch (e: any) {
      error("Couldn't save", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Skeleton className="h-96" />;
  const st = style;
  const changed = dirty || terms !== savedTerms;

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Quote design</h1>
          <p className="text-sm text-muted-foreground mt-0.5">How printed quotes look. New quotes take this design; quotes already made keep theirs.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {fromSampleOn && (
            <>
              <input ref={input} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => void fromSample(e.target.files?.[0])} aria-label="Sample quote PDF" />
              <Button size="sm" variant="outline" className="h-9 gap-1.5" onClick={() => input.current?.click()} disabled={reading}>
                {reading ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />} Make from a sample
              </Button>
            </>
          )}
          <Button size="sm" className="h-9 gap-1.5" onClick={() => void save()} disabled={busy || !changed}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save
          </Button>
        </div>
      </div>

      {reading && (
        <div className="flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm" role="status">
          <Loader2 className="size-4 animate-spin text-primary" />
          <span>
            Reading the sample quote: its colours, fonts, title and table. This can take up to a minute.
            <span className="block text-xs text-muted-foreground">The file isn't kept. Nothing is saved until you press Save.</span>
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
        <div className="xl:col-span-5 space-y-4">
          {notes && (
            <div className="flex gap-2.5 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
              <FileUp className="mt-0.5 size-4 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold">Made from your sample</div>
                <p className="text-xs text-muted-foreground">The look is copied; the items, prices and customer come from each quote. Check it, then save.</p>
                {notes.map((n, i) => (
                  <div key={i} className="mt-1 flex gap-1.5 text-xs">
                    <Info className="mt-0.5 size-3 shrink-0 text-muted-foreground" /> {n}
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setNotes(null)} aria-label="Hide" className="self-start text-muted-foreground hover:text-foreground">
                <X className="size-4" />
              </button>
            </div>
          )}

          <div className="bg-card border rounded-2xl p-4 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-1.5 text-sm font-bold">
                  <Palette className="size-4 text-primary" /> Design
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {hasLetterhead
                    ? "Quotes print on your letterhead; the design styles the text, title and tables on it."
                    : "Quotes print on a plain white page with your logo. Set up a letterhead in Settings → Company to print on it."}
                </p>
              </div>
              <label className="flex shrink-0 items-center gap-2 text-xs font-medium">
                <Switch checked={!!st} onCheckedChange={(on) => change(on ? STANDARD : null)} aria-label="Use its own design" />
                Own design
              </label>
            </div>
            {!st ? (
              <p className="rounded-lg border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">Quotes use the standard look. Turn on "Own design" or make one from a sample.</p>
            ) : (
              <>
                <div className="space-y-1">
                  <div className="text-xs font-medium">Colour sets</div>
                  <div className="flex flex-wrap gap-1.5">
                    {PALETTES.map((p) => (
                      <button
                        key={p.name}
                        type="button"
                        onClick={() => change({ ...st, colors: { ...st.colors, accent: p.accent, title: p.accent, table_head_bg: p.accent, zebra: p.zebra } })}
                        className={cn("flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs hover:bg-muted/60", st.colors.accent === p.accent && "border-primary")}
                      >
                        <span className="size-3 rounded-full" style={{ background: p.accent }} />
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2">
                  {COLOR_LABELS.map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-xs">
                      <span className="relative size-6 shrink-0 cursor-pointer rounded-md border" style={{ background: st.colors[k] }}>
                        <input type="color" value={st.colors[k]} onChange={(e) => change({ ...st, colors: { ...st.colors, [k]: e.target.value } })} aria-label={label} className="absolute inset-0 size-full cursor-pointer opacity-0" />
                      </span>
                      <span className="truncate">{label}</span>
                    </label>
                  ))}
                </div>
                <label className="block space-y-1">
                  <span className="text-xs font-medium">Title</span>
                  <input value={st.title_text} maxLength={40} onChange={(e) => set({ title_text: e.target.value })} placeholder="QUOTATION" className="h-8 w-full rounded-md border bg-background px-2 text-sm" />
                </label>
                <Choice label="Font" value={st.font} options={[["sans", "Clean"], ["serif", "Classic"], ["modern", "Modern"]]} onChange={(font) => set({ font })} />
                <Choice label="Top of the page" value={st.header} options={[["company_left", "Company left, title right"], ["title_left", "Title left, company right"], ["centered", "Centred"]]} onChange={(header) => set({ header })} />
                <Choice label="Item table" value={st.table} options={[["zebra", "Shaded rows"], ["lines", "Row lines"], ["grid", "All lines"]]} onChange={(table) => set({ table })} />
                <Choice label="Page numbers" value={st.page_numbers} options={[["bottom_right", "Bottom right"], ["bottom_center", "Bottom centre"], ["none", "None"]]} onChange={(page_numbers) => set({ page_numbers })} />
                <label className="flex items-center gap-2 text-xs">
                  <Switch checked={st.upper_labels} onCheckedChange={(upper_labels) => set({ upper_labels })} aria-label="Labels in capitals" /> Labels in capitals (BILL TO)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(
                    [
                      ["base", "Text", 8, 11],
                      ["title", "Title", 12, 30],
                    ] as const
                  ).map(([k, label, min, max]) => (
                    <label key={k} className="block space-y-1">
                      <span className="flex justify-between text-xs font-medium">
                        {label} <span className="text-muted-foreground tabular-nums">{st.sizes[k]} pt</span>
                      </span>
                      <input type="range" min={min} max={max} step={0.5} value={st.sizes[k]} onChange={(e) => set({ sizes: { ...st.sizes, [k]: Number(e.target.value) } })} className="w-full accent-primary" />
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="bg-card border rounded-2xl p-4 space-y-2">
            <div className="text-sm font-bold">Default terms and conditions</div>
            <p className="text-xs text-muted-foreground">New quotes start with these; each quote can still be changed.</p>
            <textarea value={terms} onChange={(e) => setTerms(e.target.value)} rows={6} maxLength={5000} className="w-full rounded-md border bg-background px-3 py-2 text-sm" aria-label="Default terms and conditions" />
          </div>
        </div>

        <div className="xl:col-span-7 xl:sticky xl:top-4 space-y-2">
          <div className="flex items-center gap-1.5 text-sm font-bold">
            Preview {pdfBusy && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
          </div>
          <div className="rounded-2xl border bg-muted/30 p-2">
            <p className="px-1 pb-2 text-[11px] text-muted-foreground">A sample quote with made-up items, as customers get it. Nothing here is saved.</p>
            {pdfUrl ? <iframe title="Quote preview" src={pdfUrl} className="h-[75vh] w-full rounded-lg border bg-white" /> : <Skeleton className="h-[75vh]" />}
          </div>
        </div>
      </div>
    </div>
  );
}
